import React, { useCallback, useRef, useState } from 'react';

/**
 * Toasts for the casual / supervisor availability pages.
 *
 * Every lifecycle write (`POST /api/casual/availability`, `PATCH /api/casual/assignment/:id`,
 * `POST /api/super/jobs`, `PATCH /api/super/jobs/:id`, both message routes …) answers with an
 * `email` notice — { sent, stage, stageKey, to, email, detail } — describing the automation
 * email the server tried to send. `pushEmail(r.email)` turns that into a green
 * "Email sent to {name} for {Stage}" toast, or a red one naming why it did not go out.
 *
 * HOUSE RULE — toasts are delivered from the TOP of the screen, downward. Never the
 * bottom: the bottom belongs to FooterNav and the page's own padding, and a bottom
 * toast is easy to miss on a phone. They also sit above every overlay (z-index 10200
 * vs .ca-overlay-top's 10090) so a toast fired from inside a modal — a message sent
 * from a thread, a job accepted from a record — is still visible. Any new toast in
 * this app follows the same rule: top-anchored, above the overlays.
 *
 * Several can be live at once (the action toast "Job accepted" plus its email toast),
 * so they stack downward, oldest first, rather than covering each other.
 */

const OK_MS = 4000;

export const emailToastText = (n) => {
  if (!n) return '';
  const who = n.to || 'the casual';
  const stage = n.stage || 'this stage';
  return n.sent
    ? `Email sent to ${who} for ${stage}`
    : `Email NOT sent to ${who} for ${stage}${n.detail ? ` — ${n.detail}` : ''}`;
};

export const useToasts = () => {
  const [toasts, setToasts] = useState([]);
  const seq = useRef(0);

  const dismiss = useCallback((id) => setToasts((t) => t.filter((x) => x.id !== id)), []);

  /* ms = 0 keeps the toast up until it is clicked (how errors have always behaved) */
  const push = useCallback((text, kind = 'ok', ms = OK_MS) => {
    if (!text) return;
    seq.current += 1;
    const id = seq.current;
    setToasts((t) => [...t, { id, text: String(text), kind }]);
    if (ms > 0) setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), ms);
  }, []);

  const fail = useCallback((e) => push(e?.message || 'Something went wrong', 'err', 0), [push]);

  /* one toast per automation email the server reports back; a failure stays up until clicked */
  const pushEmail = useCallback((notice) => {
    if (!notice) return;
    push(emailToastText(notice), notice.sent ? 'ok' : 'err', notice.sent ? OK_MS : 0);
  }, [push]);

  return { toasts, push, pushEmail, fail, dismiss };
};

export const ToastStack = ({ toasts = [], onDismiss }) => {
  if (!toasts.length) return null;
  return (
    <div className="ca-toast-stack" role="status" aria-live="polite">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`ca-toast${t.kind === 'err' ? ' ca-toast-error' : ''}`}
          onClick={() => onDismiss(t.id)}
        >
          {t.text}
        </div>
      ))}
    </div>
  );
};

export default ToastStack;
